import React from 'react';
import { Button } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';

export interface UnauthorizedProps {
  /**
   * The title to display at the top of the unauthorized message
   * @default "Access Restricted"
   */
  title?: string;

  /**
   * The message to display explaining why access is restricted
   * @default "You don't have permission to access this resource."
   */
  message?: string;

  /**
   * The text for the primary action button
   * @default "Go Back"
   */
  primaryActionText?: string;

  /**
   * The function to call when the primary action button is clicked
   * @default () => window.history.back()
   */
  onPrimaryAction?: () => void;

  /**
   * Whether to show a "Contact Support" button
   * @default false
   */
  showContactSupport?: boolean;

  /**
   * The email address to contact for support
   * @default "support@medscout.com"
   */
  supportEmail?: string;
}

/**
 * A reusable component for displaying access restriction messages in a modern, full-page layout
 */
const Unauthorized: React.FC<UnauthorizedProps> = ({
  title = 'Access Restricted',
  message = "You don't have permission to access this resource.",
  primaryActionText = 'Go Back',
  onPrimaryAction,
  showContactSupport = false,
  supportEmail = 'support@medscout.com',
}) => {
  const handlePrimaryAction = () => {
    if (onPrimaryAction) {
      onPrimaryAction();
    } else {
      window.history.back();
    }
  };

  const handleContactSupport = () => {
    window.location.href = `mailto:${supportEmail}?subject=Access Request&body=I need access to: ${window.location.href}`;
  };

  return (
    <div className="h-[calc(100vh-18rem)] w-full flex items-center justify-center bg-gradient-to-b from-neutral-50 to-neutral-100 relative overflow-hidden rounded-xl">
      {/* Decorative background pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(30deg,rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(150deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative w-full max-w-lg px-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-sky-100 p-8 animate-fade-in">
          {/* Decorative top accent */}
          <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-sky-300 to-transparent opacity-50" />

          <div className="flex flex-col items-center gap-8">
            {/* Warning Icon */}
            <div className="rounded-full bg-gradient-to-br from-sky-50 to-sky-100 p-4 shadow-inner ring-1 ring-sky-200/50">
              <Icon
                icon={faTriangleExclamation}
                className="h-8 w-8 text-sky-600 animate-pulse"
                aria-hidden="true"
              />
            </div>

            {/* Title and Message */}
            <div className="flex flex-col items-center gap-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-neutral-800 tracking-tight">
                {title}
              </h2>
              <p className="text-neutral-600 leading-relaxed max-w-md text-center">
                {message}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 w-full">
              <Button
                size="lg"
                variant="outlined"
                color="primary"
                onClick={handlePrimaryAction}
                className="w-full rounded-full sm:w-auto bg-white hover:bg-sky-50 border-sky-200 hover:border-sky-300 text-sky-600 shadow-sm hover:shadow-md transition-all duration-200"
              >
                {primaryActionText}
              </Button>

              {showContactSupport && (
                <Button
                  size="lg"
                  variant="plain"
                  color="primary"
                  onClick={handleContactSupport}
                  className="w-full rounded-full sm:w-auto text-neutral-600 hover:text-sky-600 hover:bg-sky-50/50 transition-colors duration-200"
                >
                  Contact Support
                </Button>
              )}
            </div>
          </div>

          {/* Decorative bottom accent */}
          <div className="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-sky-300 to-transparent opacity-50" />
        </div>
      </div>
    </div>
  );
};

export default Unauthorized;
